/**
 * `<JdDeleteConfirmModal>` — Phase 2.3 type-to-confirm destructive modal.
 *
 * Spec: design/post-walkthrough/jd-rebuild-spec.md §5 +
 * global-patterns-spec.md §1.4 (high-blast-radius destructive pattern).
 *
 * Behavior:
 *  - Headline: "Delete this job?"
 *  - Body interpolates evaluation count (or short-form when 0).
 *  - Type-to-confirm field: literal job title; destructive button enables
 *    only on exact match (case-sensitive, trimmed of leading/trailing
 *    whitespace per founder's "feels deliberate" call).
 *  - Default focus on Cancel (the safe action) per global-patterns §1.4.
 *  - Esc → cancel; click outside backdrop → cancel.
 */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { Button } from "@/components/common/Button";

/* Cancel button is auto-focused on mount via querySelector on the panel
 * ref + a data attribute, matching the pattern used by the global Modal
 * primitive (Button is not forwardRef-capable). */

interface JdDeleteConfirmModalProps {
  jdName: string;
  /** Number of candidate evaluations against this JD; copy varies on 0 vs >0. */
  evaluationCount?: number;
  onCancel: () => void;
  onConfirm: () => void;
  pending?: boolean;
}

export function JdDeleteConfirmModal({
  jdName,
  evaluationCount = 0,
  onCancel,
  onConfirm,
  pending = false,
}: JdDeleteConfirmModalProps): JSX.Element {
  const [confirmInput, setConfirmInput] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Focus the safe action on mount per spec — same pattern as
  // Modal.tsx (Button is not forwardRef-capable).
  useEffect(() => {
    const t = window.setTimeout(() => {
      const safe = panelRef.current?.querySelector<HTMLButtonElement>(
        "[data-modal-safe]",
      );
      safe?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const matches = confirmInput.trim() === jdName.trim();

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (!pending) onCancel();
    }
  };

  const body =
    evaluationCount > 0
      ? `${jdName} will be permanently removed, along with ${evaluationCount} candidate ${
          evaluationCount === 1 ? "evaluation" : "evaluations"
        } and notes against this job. The candidates themselves will remain in your tenant.`
      : `${jdName} will be permanently removed. No candidates have been evaluated against it yet.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jd-delete-title"
      aria-describedby="jd-delete-body"
      onKeyDown={handleKeyDown}
      onClick={() => {
        if (!pending) onCancel();
      }}
    >
      <div
        ref={panelRef}
        className="w-full max-w-md rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="jd-delete-modal"
      >
        <h2
          id="jd-delete-title"
          className="mb-2 text-lg font-semibold text-text-emphasis"
        >
          Delete this job?
        </h2>
        <p
          id="jd-delete-body"
          className="mb-5 text-sm leading-relaxed text-text-secondary"
        >
          {body}
        </p>

        <div className="mb-5">
          <label
            htmlFor="jd-delete-confirm-input"
            className="mb-1.5 block text-xs font-medium text-text-secondary"
          >
            Type{" "}
            <span className="font-semibold text-text-primary">
              &lsquo;{jdName}&rsquo;
            </span>{" "}
            to confirm
          </label>
          <input
            id="jd-delete-confirm-input"
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={`Type '${jdName}' to confirm`}
            className="block w-full rounded-md border border-border-strong bg-surface-subtle px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-[var(--border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--border-focus)]"
            data-testid="jd-delete-confirm-input"
            disabled={pending}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            data-modal-safe
            variant="secondary"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!matches || pending}
            loading={pending}
            data-testid="jd-delete-confirm-button"
          >
            Delete permanently
          </Button>
        </div>
      </div>
    </div>
  );
}
